import { ClaudeCredentialManager } from '@/components/claude-credential-manager';
import { claudeCredentialsApi } from '@/lib/api';
import type { CreateClaudeCredentialInput } from '@mss_creator/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

export const Route = createFileRoute('/admin/claude-credentials')({
  component: ClaudeCredentialsPage,
});

/**
 * pms_admin-role gating is enforced server-side (403 on every route); this page assumes it's only
 * ever linked to from the pms_admin-gated rail item in AppRail.
 *
 * Exported (rather than kept file-local like most other route components) so
 * tests/admin-claude-credentials.test.tsx can render it directly under a
 * QueryClientProvider without needing a full router harness — it has no
 * router-specific hooks (no params/search/navigate), so this is safe.
 */
export function ClaudeCredentialsPage() {
  const queryClient = useQueryClient();
  const credentialsQuery = useQuery({
    queryKey: ['claude-credentials'],
    queryFn: () => claudeCredentialsApi.list(),
  });

  const invalidateCredentials = () =>
    queryClient.invalidateQueries({ queryKey: ['claude-credentials'] });

  const addMutation = useMutation({
    mutationFn: (input: CreateClaudeCredentialInput) => claudeCredentialsApi.create(input),
    onSuccess: invalidateCredentials,
  });
  const activateMutation = useMutation({
    mutationFn: (id: string) => claudeCredentialsApi.activate(id),
    onSuccess: invalidateCredentials,
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => claudeCredentialsApi.delete(id),
    onSuccess: invalidateCredentials,
  });

  // Only take over the whole page when there's genuinely nothing to show yet.
  // A background refetch failure (e.g. an activate/delete's invalidateQueries
  // triggering a refetch that hits a transient network blip) still has the
  // last-good `data` sitting in the cache — blanking the page in that case
  // would throw away a perfectly good, already-loaded UI. See the small
  // inline hint below instead, kept alongside the existing content.
  if (credentialsQuery.isError && !credentialsQuery.data) {
    return (
      <p className="text-danger text-small">
        Impossible de charger les identifiants Claude.
        {credentialsQuery.error instanceof Error ? ` ${credentialsQuery.error.message}` : ''}
      </p>
    );
  }

  if (!credentialsQuery.data) {
    return <p className="text-gray-500 text-small">Chargement…</p>;
  }

  return (
    <section className="mx-auto max-w-3xl space-y-4 px-6 py-8">
      <h1 className="font-bold text-bleu-nuit text-h2">Identifiants Claude</h1>
      <div className="space-y-2 rounded-md border border-info/30 bg-info-tint px-4 py-3 text-small">
        <p className="font-semibold text-info">Comment ajouter un identifiant</p>
        <ol className="list-decimal space-y-1 pl-4">
          <li>
            Sur n'importe quelle machine avec le CLI <code>claude</code> installé et un navigateur
            (pas besoin que ce soit le serveur), lancez <code>claude setup-token</code>, terminez la
            connexion dans le navigateur, puis copiez le jeton affiché.
          </li>
          <li>
            Ci-dessous, donnez un alias à votre choix, sélectionnez « Compte Claude personnel
            (oauth_token) », puis collez ce jeton dans le champ Secret.
          </li>
          <li>
            Une fois ajouté, l'identifiant n'est pas actif automatiquement — cliquez sur « Activer
            ».
          </li>
        </ol>
      </div>
      {credentialsQuery.isError && (
        <p className="text-danger text-small">
          Échec de l'actualisation des identifiants Claude — affichage des dernières données
          connues.
        </p>
      )}
      <ClaudeCredentialManager
        credentials={credentialsQuery.data}
        onAdd={async (input) => {
          await addMutation.mutateAsync(input);
        }}
        onActivate={(id) => activateMutation.mutateAsync(id)}
        onDelete={(id) => deleteMutation.mutateAsync(id)}
      />
    </section>
  );
}
